import { useEffect, useState } from 'react';
import { Link, Navigate, NavLink, Outlet, useLocation } from 'react-router-dom';
import { KeyRound, PackageCheck, LayoutGrid, HardDrive, CreditCard, BellRing, Sparkles, Archive, MonitorSmartphone, Users, UserCog, LogOut, Menu, X, ArrowUpRight, GraduationCap } from 'lucide-react';
import { useSession } from '@/lib/session';
import { INK_U_URL } from '@/lib/site';
import { Loading } from './components';

const NAV = [
  { to: '/portal', label: 'Overview', icon: LayoutGrid, end: true, viewer: false },
  { to: '/portal/appliances', label: 'Appliances', icon: HardDrive },
  { to: '/portal/licenses', label: 'Licenses', icon: KeyRound },
  { to: '/portal/updates', label: 'Software Updates', icon: PackageCheck },
  { to: '/portal/billing', label: 'Plans & Billing', icon: CreditCard },
  { to: '/portal/notifications', label: 'SMS & Voice', icon: BellRing },
  { to: '/portal/ai', label: 'AI Usage', icon: Sparkles },
  { to: '/portal/archive', label: 'Data Archive', icon: Archive },
  { to: '/portal/remote', label: 'Remote Viewing', icon: MonitorSmartphone, viewer: true },
  { to: '/portal/app-users', label: 'App users', icon: Users },
  { to: '/portal/account', label: 'Account', icon: UserCog, viewer: true },
];

export function PortalLayout() {
  const { session, loading, logout, verify } = useSession();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // A customer on a new device has no sign-in hint, so ask the cloud directly.
  useEffect(() => {
    if (!session) verify();
  }, [session, verify]);
  const [checked, setChecked] = useState(false);
  useEffect(() => {
    if (!loading) setChecked(true);
  }, [loading]);

  if (loading || (!session && !checked)) {
    return (
      <div className="min-h-screen bg-ink-base">
        <Loading />
      </div>
    );
  }
  if (!session) {
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }

  // A remote viewer's account holds only remote views; every other page would
  // just be refused by the cloud.
  const isViewer = session.user.role === 'viewer';
  const allowed = (path: string) => !isViewer || NAV.some((n) => n.viewer && (path === n.to || path.startsWith(`${n.to}/`)));
  if (isViewer && !allowed(location.pathname)) return <Navigate to="/portal/remote" replace />;

  const nav = (
    <nav className="space-y-0.5" aria-label="Portal">
      {NAV.filter((item) => !isViewer || item.viewer).map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={() => setOpen(false)}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 rounded-[var(--radius-glass-sm)] text-sm font-medium transition-colors ${
              isActive ? 'bg-brand-primary/10 text-brand-primary' : 'text-text-secondary hover:bg-ink-muted hover:text-text-primary'
            }`
          }
        >
          <item.icon size={18} />
          {item.label}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-ink-base">
      <header className="sticky top-0 z-40 h-16 bg-white border-b border-glass-border">
        <div className="h-full px-4 lg:px-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button type="button" className="lg:hidden p-2 -ml-2" onClick={() => setOpen(!open)} aria-label={open ? 'Close menu' : 'Open menu'}>
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
            <Link to="/portal" className="flex items-center gap-3">
              <img src="/scada-ink-logo.png" alt="Scada.Ink" className="h-10 w-auto" />
              <span className="hidden sm:inline text-xs font-semibold uppercase tracking-[0.16em] text-text-muted border-l border-glass-border pl-3">Customer Portal</span>
            </Link>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="hidden md:inline text-sm text-text-secondary hover:text-text-primary">
              scada.ink
            </Link>
            <div className="hidden sm:block text-right">
              <div className="text-sm font-semibold leading-tight">{session.user.name}</div>
              <div className="text-xs text-text-muted leading-tight">{session.account.company}</div>
            </div>
            <button
              type="button"
              onClick={() => void logout()}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[var(--radius-glass-sm)] border border-glass-border text-sm text-text-secondary hover:text-text-primary hover:border-glass-border-bright"
            >
              <LogOut size={15} /> <span className="hidden sm:inline">Log out</span>
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        <aside className={`${open ? 'fixed inset-0 top-16 z-30 bg-white' : 'hidden'} lg:block lg:sticky lg:top-16 lg:h-[calc(100vh-4rem)] w-full lg:w-60 shrink-0 border-r border-glass-border bg-white p-4 overflow-y-auto`}>
          {nav}
          <div className="mt-6 rounded-[var(--radius-glass-sm)] bg-gradient-to-br from-[#eef2ff] to-[#f5f3ff] border border-glass-border p-4">
            <GraduationCap size={18} className="text-brand-secondary" />
            <div className="mt-2 text-sm font-semibold">Train your team</div>
            <div className="text-xs text-text-secondary mt-0.5">Free courses & certification on Ink U.</div>
            <a href={INK_U_URL} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-brand-primary">
              Open Ink U <ArrowUpRight size={12} />
            </a>
          </div>
        </aside>
        <main className="flex-1 min-w-0 px-4 py-8 lg:px-10 lg:py-10">
          <div className="max-w-6xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
